import { CUSTOM_CSS_SCOPE_PREFIX } from '@opengov/stories-shared/utils/story';

type ElementWithSelectorMethods = HTMLElement & {
  webkitMatchesSelector?: (selector: string) => boolean;
  mozMatchesSelector?: (selector: string) => boolean;
  msMatchesSelector?: (selector: string) => boolean;
  oMatchesSelector?: (selector: string) => boolean;
};

export const HIGHLIGHT_ELEMENT_CLASSNAME = 'highlight-element';
export const HIGHLIGHT_ACTIVE_ELEMENT_CLASSNAME = 'highlight-active-element';
export const SYSTEM_PROTECTED_CLASSNAMES = [HIGHLIGHT_ELEMENT_CLASSNAME, HIGHLIGHT_ACTIVE_ELEMENT_CLASSNAME];

export const PUBLICATION_CUSTOM_CSS_ID_ATTR = 'publication-custom-css';
export const STORY_CUSTOM_CSS_ID_ATTR = 'story-custom-css';
export const SYSTEM_PROTECTED_ID_ATTRS = [PUBLICATION_CUSTOM_CSS_ID_ATTR, STORY_CUSTOM_CSS_ID_ATTR];

export const getValidClassNames = (element: HTMLElement): string[] =>
  Array.from(element.classList).filter((cl) => !SYSTEM_PROTECTED_CLASSNAMES.includes(cl));

export const getElementPossibleSelectors = (element: HTMLElement | null): string[] => {
  try {
    const selectors: string[] = [];
    if (!element) return selectors;
    if (element.id && !element.id.includes(CUSTOM_CSS_SCOPE_PREFIX)) selectors.push(`#${element.id}`);
    selectors.push(...getValidClassNames(element).map((cl) => `.${cl}`));
    if (!selectors.length) {
      const chain = [element.tagName.toLowerCase()];
      let currentEl: HTMLElement | null = element;
      while (currentEl) {
        const parent = currentEl.parentElement;
        if (!parent || parent.tagName === 'HTML') {
          currentEl = null;
        } else {
          const classes = getValidClassNames(parent);
          if (classes.length) {
            classes.forEach((cl) => {
              selectors.push(`.${cl} ${chain.join(' ')}`);
            });
            currentEl = null;
          } else if (parent.id && !parent.id.includes(CUSTOM_CSS_SCOPE_PREFIX)) {
            selectors.push(`#${parent.id} > ${chain.join(' > ')}`);
            currentEl = null;
          } else {
            chain.unshift(parent.tagName.toLowerCase());
            currentEl = parent;
          }
        }
      }
    }
    return selectors;
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const beautifySingleLineCssText = (cssText: string): string =>
  cssText.split('{').join(`{\n `).split(';').join(`;\n `).split(`  }`).join('}');

export const getElementInheritStyles = (element: ElementWithSelectorMethods, stylesheets: CSSStyleSheet[]): string => {
  try {
    if (!element) return '';
    const styles: string[] = [];
    element.matches =
      element.matches ||
      element.webkitMatchesSelector ||
      element.mozMatchesSelector ||
      element.msMatchesSelector ||
      element.oMatchesSelector;
    stylesheets.forEach((stylesheet) => {
      const rules = stylesheet.cssRules || stylesheet.rules;
      Object.values(rules as CSSRuleList).map((cssRule: CSSRule) => {
        const rule = cssRule as CSSStyleRule;
        if (
          !rule.selectorText?.includes(HIGHLIGHT_ELEMENT_CLASSNAME) &&
          !rule.selectorText?.includes(HIGHLIGHT_ACTIVE_ELEMENT_CLASSNAME) &&
          element.matches(rule.selectorText)
        ) {
          styles.push(beautifySingleLineCssText(rule.cssText));
        }
      });
    });
    return !styles.length ? '' : `${styles.join(`\n`)}\n`;
  } catch {
    return '';
  }
};

export const getElementInlineStyles = (element: HTMLElement): string => {
  let cssString = '';
  try {
    const inlineStyles = [...Object.entries(element.style)].filter(([prop, value]: any) => !!value && isNaN(prop));
    if (inlineStyles.length) {
      cssString += `element.style {\n`;
      inlineStyles.forEach(([prop, value]) => {
        if (value) cssString += `  ${prop.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}: ${value};\n`;
      });
      cssString += '}\n';
    }
    return cssString;
  } catch (e) {
    return cssString;
  }
};

type OwnerNode = Element & { dataset?: { id?: string } };

export const getElementFullStylesheet = (element: HTMLElement | null, iframeDocument: Document | null): string => {
  let cssString = '';
  try {
    if (element) {
      cssString += getElementInlineStyles(element);
      if (iframeDocument) {
        const stylesheets = Array.from(iframeDocument.styleSheets).filter((stylesheet) => {
          try {
            if (SYSTEM_PROTECTED_ID_ATTRS.includes((stylesheet.ownerNode as OwnerNode)?.dataset?.id as string)) {
              return false;
            }
            return stylesheet.cssRules || stylesheet.rules;
          } catch (error) {
            return false;
          }
        });
        cssString += getElementInheritStyles(element, stylesheets as CSSStyleSheet[]);
      }
      return cssString;
    }
    return cssString;
  } catch (error) {
    console.error(error);
    return cssString;
  }
};

export const getElementAttributes = (element: Element): string => {
  let attributes = '';
  if (element.id && !element.id.includes(CUSTOM_CSS_SCOPE_PREFIX)) {
    attributes += ` id="${element.id}"`;
  }
  if (element.classList.length) {
    attributes += ` class="${getValidClassNames(element as HTMLElement).join(' ')}"`;
  }
  return attributes;
};
